import { useState, useEffect } from 'react';
import { Dialog, DialogContent, DialogDescription, DialogFooter, DialogHeader, DialogTitle } from '@/components/ui/dialog';
import { Button } from '@/components/ui/button';
import { TagBadge } from '@/components/tags/TagBadge';
import type { Tag } from '@/types/database';
import { CircleDot } from 'lucide-react';

interface TagsDialogProps {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  currentTags?: string[]; // For single entity
  partialTags?: string[]; // For bulk: tags some entities have
  commonTags?: string[]; // For bulk: tags all selected entities have
  availableTags: Tag[];
  onSave: (tags: string[]) => void;
  isBulkMode?: boolean;
  entityName?: string; // For display in title
  selectedCount?: number; // For bulk mode display
}

/**
 * Dialog for editing tags on a single entity or multiple entities (bulk mode)
 * In bulk mode, shows common tags (all entities have) and partial tags (some have)
 */
export function TagsDialog({
  open,
  onOpenChange,
  currentTags = [],
  partialTags = [],
  commonTags = [],
  availableTags,
  onSave,
  isBulkMode = false,
  entityName,
  selectedCount = 0
}: TagsDialogProps) {
  const [selectedTags, setSelectedTags] = useState<string[]>(
    isBulkMode ? commonTags : currentTags
  );

  // Reset selected tags ONLY when dialog opens (not on every currentTags/commonTags change)
  useEffect(() => {
    if (open) {
      setSelectedTags(isBulkMode ? commonTags : currentTags);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [open]);

  const handleToggle = (tagName: string) => {
    setSelectedTags(prev =>
      prev.includes(tagName)
        ? prev.filter(t => t !== tagName)
        : [...prev, tagName]
    );
  };

  const handleSave = () => {
    onSave(selectedTags);
    onOpenChange(false);
  };

  // Check if a tag is partially selected (bulk mode only)
  const isPartial = (tagName: string) =>
    isBulkMode && partialTags.includes(tagName) && !selectedTags.includes(tagName);

  const standardTags = availableTags.filter(t => t.is_standard);
  const customTags = availableTags.filter(t => !t.is_standard);

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="max-w-2xl">
        <DialogHeader>
          <DialogTitle>
            {isBulkMode ? `Edit Tags (${selectedCount} selected)` : 'Edit Tags'}
          </DialogTitle>
          <DialogDescription>
            {isBulkMode ? (
              <span>
                Tags with <CircleDot className="inline h-3 w-3 mx-1" /> are applied to some but not all selected items
              </span>
            ) : entityName ? (
              <>
                Manage tags for <span className="font-semibold">{entityName}</span>
              </>
            ) : (
              'Manage tags for this item'
            )}
          </DialogDescription>
        </DialogHeader>

        <div className="space-y-4">
          {/* Current/Selected Tags */}
          <div className="space-y-2">
            <label className="text-sm font-medium">
              {isBulkMode ? 'Common Tags (applied to all)' : 'Current Tags'}
            </label>
            <div className="flex flex-wrap gap-2 min-h-[40px] p-3 border rounded-md bg-muted/30">
              {selectedTags.length > 0 ? (
                selectedTags.map((tagName) => {
                  const tag = availableTags.find((t) => t.name === tagName);
                  return (
                    <TagBadge
                      key={tagName}
                      tag={tag || tagName}
                      color={tag?.color}
                      onRemove={() => handleToggle(tagName)}
                    />
                  );
                })
              ) : (
                <span className="text-sm text-muted-foreground">No tags selected</span>
              )}
            </div>
          </div>

          {/* Available Tags */}
          <div className="space-y-2">
            <label className="text-sm font-medium">Available Tags</label>
            <div className="border rounded-md p-3 max-h-[300px] overflow-y-auto">
              {availableTags && availableTags.length > 0 ? (
                <div className="space-y-3">
                  {/* Standard Tags */}
                  {standardTags.length > 0 && (
                    <div>
                      <p className="text-xs font-medium text-muted-foreground mb-2">Standard Tags</p>
                      <div className="flex flex-wrap gap-2">
                        {standardTags.map((tag) => {
                          const isSelected = selectedTags.includes(tag.name);
                          const isPartialTag = isPartial(tag.name);

                          return (
                            <button
                              key={tag.id}
                              type="button"
                              onClick={(e) => {
                                e.stopPropagation();
                                handleToggle(tag.name);
                              }}
                              className={`transition-opacity relative cursor-pointer ${
                                isSelected ? 'opacity-40' : ''
                              }`}
                            >
                              <span className="pointer-events-none">
                                <TagBadge tag={tag} />
                              </span>
                              {isPartialTag && (
                                <CircleDot className="absolute -top-1 -right-1 h-3 w-3 text-orange-500 fill-white" />
                              )}
                            </button>
                          );
                        })}
                      </div>
                    </div>
                  )}

                  {/* Custom Tags */}
                  {customTags.length > 0 && (
                    <div className={standardTags.length > 0 ? 'pt-2 border-t' : ''}>
                      <p className="text-xs font-medium text-muted-foreground mb-2">Custom Tags</p>
                      <div className="flex flex-wrap gap-2">
                        {customTags.map((tag) => {
                          const isSelected = selectedTags.includes(tag.name);
                          const isPartialTag = isPartial(tag.name);

                          return (
                            <button
                              key={tag.id}
                              type="button"
                              onClick={(e) => {
                                e.stopPropagation();
                                handleToggle(tag.name);
                              }}
                              className={`transition-opacity relative cursor-pointer ${
                                isSelected ? 'opacity-40' : ''
                              }`}
                            >
                              <span className="pointer-events-none">
                                <TagBadge tag={tag} />
                              </span>
                              {isPartialTag && (
                                <CircleDot className="absolute -top-1 -right-1 h-3 w-3 text-orange-500 fill-white" />
                              )}
                            </button>
                          );
                        })}
                      </div>
                    </div>
                  )}
                </div>
              ) : (
                <p className="text-sm text-muted-foreground text-center py-4">
                  No tags available. Create tags in the Tags section first.
                </p>
              )}
            </div>
          </div>
        </div>

        <DialogFooter>
          <Button variant="outline" onClick={() => onOpenChange(false)}>
            Cancel
          </Button>
          <Button onClick={handleSave}>
            Save Changes
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}
